import { useMemo, useState } from 'react'
import { dateStampFormat } from 'shared/util'

import { useListWebLog, useListWebLogService, useListWebLogTimes } from "interface/kojin/log"

import { Alert, Box, BoxProps, Stack, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material"

import { Section } from 'components/controls/section'
import { Loading } from 'components/controls/loading'
import { ErrorInfo } from 'components/controls/errorInfo'
import { LimittedStack } from 'components/controls/limittedStack'
import { BlockCaption } from 'components/controls/blockCaption'

import { AccessTimeHeatMap } from 'features/kojin/components/accessTimeHeatMap'

import { LogRecord } from "./components/logRecord"
import { ServiceChips } from './components/serviceChip'
import { useKojinCareNetTag } from './container/tags'
import { limit_client_count, limit_tag_count } from './constants'

import { useKojinPreview } from '../../container'
import { KojinResult } from 'interface/kojin'

const limitLogCountListCandidates = [20, 50, 100]

type KojinCareNetPageCoreProps = {
  kojin: KojinResult
  isSmallMedia: boolean
} & BoxProps
const KojinCareNetPageCore = ({
  kojin,
  isSmallMedia,
  ...others
}: KojinCareNetPageCoreProps) => {
  const [service, setService] = useState<string[]>([])
  const [hour, setHour] = useState<number[]>([])
  const [weekDay, setWeekDay] = useState<string[]>([])
  const [limitLogCount, setLimitLogCount] = useState<number>(20)
  
  const timesState = useListWebLogTimes(kojin.personalId, service)
  const servicesState = useListWebLogService(kojin.personalId, hour, weekDay)

  const tagCondition = useMemo(
    () => ({
      service,
      hour,
      weekDay
    }),
    [service, hour, weekDay]
  )
  const tagState = useKojinCareNetTag({ personalId: kojin.personalId, condition: tagCondition })

  const logCondition = useMemo(
    () => ({
      service,
      hour,
      weekDay,
      offset: 0,
      limit: limitLogCount
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [service, hour, weekDay, limitLogCount]
  )
  const logState = useListWebLog(kojin.personalId, logCondition)

  const logList = useMemo(
    () => {
      let prevDate = ''
      return logState.data?.list.map(log => {
        let dateLabel = ''
        if (log.firstEventDate !== prevDate) {
          prevDate = log.firstEventDate
          dateLabel = dateStampFormat(log.firstEventDate)
        }
        return { ...log, dateLabel }
      }) ?? []
    },
    [logState.data]
  )
  return (
    <Box height="100%" {...others}>
      {(timesState.loading || servicesState.loading || tagState.loading || logState.loading) && <Loading/>}

      {timesState.error && <ErrorInfo error={timesState.error} />}
      {servicesState.error && <ErrorInfo error={servicesState.error} />}
      {tagState.error && <ErrorInfo error={tagState.error} />}
      {logState.error && <ErrorInfo error={logState.error} />}

      <AccessTimeHeatMap
        list={timesState.data?.list ?? []}
        currentHour={hour}
        currentWeekDay={weekDay}
        setCurrent={(weekDay, hour) => {
          setHour(hour)
          setWeekDay(weekDay)
        }}
        height={200}
      />

      <ServiceChips
        services={servicesState.data?.list ?? []}
        currentService={service}
        setCurrentService={setService}
      />

      {tagState.clientList.length > 0 && (
        <Section label="製薬会社">
          <LimittedStack
            keyPrefix='client'
            direction='row' flexWrap="wrap" alignItems="cener"
            list={tagState.clientList}
            count={limit_client_count}
            record={(client, i, arr) => (
              <Typography
                key={`client-${i}`}
                variant="body2"
                component="span"
              >
                {client.tag}
                {(i < (arr.length - 1)) ? "、" : ""}
              </Typography>
            )}
          />
        </Section>
      )}

      {tagState.departmentList.length > 0 && (
        <Section label="診療科タグ">
          <LimittedStack
            keyPrefix='department'
            direction='row' flexWrap="wrap" alignItems="cener"
            list={tagState.departmentList}
            count={limit_tag_count}
            record={(department, i, arr) => (
              <Typography
                key={`tag-department-${i}`}
                variant="body2"
                component="span"
                sx={{ color: (theme) => theme.palette.secondary.main }}
              >
                {department.tag}
                {(i < (arr.length - 1)) ? "、" : ""}
              </Typography>
            )}
          />
        </Section>
      )}            
      {tagState.diseaseList.length > 0 && (
        <Section label="疾患タグ">
          <LimittedStack
            keyPrefix='disease'
            direction='row' flexWrap="wrap" alignItems="cener"
            list={tagState.diseaseList}
            count={limit_tag_count}
            record={(disease, i, arr) => (
              <Typography
                key={`tag-disease-${i}`}
                variant="body2"
                component="span"
                sx={{ color: (theme) => theme.palette.primary.main }}
              >
                {disease.tag}
                {(i < (arr.length - 1)) ? "、" : ""}
              </Typography>
            )}
          />
        </Section>
      )}
      {tagState.drugList.length > 0 && (
        <Section label="薬品タグ">
          <LimittedStack
            keyPrefix='drug'
            direction='row' flexWrap="wrap" alignItems="cener"
            list={tagState.drugList}
            count={limit_tag_count}
            record={(drug, i, arr) => (
              <Typography
                key={`tag-drug-${i}`}
                variant="body2"
                component="span"
                fontWeight={drug.isTarget ? 'bold' : undefined}
                sx={{ color: (theme) => theme.palette.success.main }}
              >
                {drug.tag}
                {(i < (arr.length - 1)) ? "、" : ""}
              </Typography>
            )}
          />
        </Section>
      )}
      {tagState.otherTagList.length > 0 && (
        <Section label="その他タグ">
          <LimittedStack
            keyPrefix='other'
            direction='row' flexWrap="wrap" alignItems="cener"
            list={tagState.otherTagList}
            count={limit_tag_count}
            record={(other, i, arr) => (
              <Typography
                key={`tag-other-${i}`}
                variant="body2"
                component="span"
                sx={{ color: (theme) => theme.palette.warning.main }}
              >
                {other.tag}
                {(i < (arr.length - 1)) ? "、" : ""}
              </Typography>
            )}
          />
        </Section>
      )}

      {logState.data && (
        <Box>
          <BlockCaption
            caption="閲覧履歴"
            rightElement={
              <Stack direction="row" alignItems="baseline" spacing={1} paddingBottom={0.5}>
                <Typography variant='caption'>最大表示件数</Typography>
                <ToggleButtonGroup
                  size="small"
                  value={limitLogCount}
                  exclusive
                  onChange={(ev, v) => setLimitLogCount(v)}
                >
                  {limitLogCountListCandidates.map(count => (
                    <ToggleButton
                      key={`limit-${count}`}
                      value={count}
                      sx={{ paddingX: 1, paddingY: 0 }}
                    >
                      {count}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Stack>
            }
          />
          {logList.length === 0 ? (
            <Box padding={1}>
              <Alert severity="info">閲覧履歴がありません</Alert>
            </Box>
          ) : logList.map((log, i) => (
            <LogRecord
              key={`log-${i}`}
              isSmallMedia={isSmallMedia}
              dateLabel={log.dateLabel}
              log={log}
            />
          ))}
        </Box>
      )}
    </Box>
  )
}

type KojinCareNetPageProps = {
  container: ReturnType<typeof useKojinPreview>
  isSmallMedia: boolean
} & BoxProps
export const KojinCareNetPage = ({
  container: {
    kojin
  },
  isSmallMedia,
  ...others
}: KojinCareNetPageProps) => (!kojin || !kojin.personalId) ? (
  <Box padding={1} {...others}>
    <Alert severity="info" sx={{ wordBreak: 'break-all' }}>
      非会員の医師です
    </Alert>
  </Box>
) : (
  <KojinCareNetPageCore
    kojin={kojin}
    isSmallMedia={isSmallMedia}
    {...others}
  />
)